import type { GlobalRole, ProjectRole } from './roles.js';

/* ------------------------------------------------------------------ */
/* Primitives                                                          */
/* ------------------------------------------------------------------ */

export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export type HttpMethod = (typeof HTTP_METHODS)[number];

export type Json = string | number | boolean | null | Json[] | { [k: string]: Json };

export interface Viewport {
  x: number;
  y: number;
  zoom: number;
}

/* ------------------------------------------------------------------ */
/* Flow graph                                                          */
/* ------------------------------------------------------------------ */

export type NodeKind =
  | 'trigger.http'
  | 'auth.guard'
  | 'validate.schema'
  | 'datasource.query'
  | 'http.request'
  | 'transform.js'
  | 'transform.map'
  | 'logic.condition'
  | 'logic.switch'
  | 'logic.foreach'
  | 'state.set'
  | 'util.delay'
  | 'util.log'
  | 'realtime.publish'
  | 'response';

/** A field descriptor used by validate/trigger nodes and by schema inference. */
export interface FieldSpec {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array' | 'any';
  required?: boolean;
  default?: Json;
  description?: string;
}

export interface TriggerHttpData {
  method: HttpMethod;
  path: string; // e.g. /users/:id
  /** How the caller proves identity when invoking this endpoint. */
  authMode: 'public' | 'token' | 'session' | 'either';
  /** Minimum project role required. Only meaningful for session/either auth. */
  minRole?: ProjectRole;
  /** Token scopes required when invoked with an API token. */
  scopes?: TokenScope[];
  query?: FieldSpec[];
  body?: FieldSpec[];
  description?: string;
}

export interface AuthGuardData {
  minRole?: ProjectRole;
  scopes?: TokenScope[];
  /** Optional expression that must evaluate truthy, e.g. {{ user.id }} === {{ input.params.id }} */
  expression?: string;
  denyStatus?: number;
  denyMessage?: string;
}

export interface ValidateSchemaData {
  target: 'body' | 'query' | 'params' | 'expression';
  expression?: string;
  fields: FieldSpec[];
  stripUnknown?: boolean;
  failStatus?: number;
}

export interface DatasourceQueryData {
  datasourceId: string | null;
  /** SQL with :named params, or a mongo-ish JSON body depending on datasource kind. */
  sql: string;
  /** name -> template expression, resolved before binding. */
  params: Record<string, string>;
  /** Return only the first row. */
  single?: boolean;
  timeoutMs?: number;
}

export interface HttpRequestData {
  method: HttpMethod;
  url: string;
  headers: Record<string, string>;
  query: Record<string, string>;
  bodyMode: 'none' | 'json' | 'text' | 'form';
  body: string;
  timeoutMs?: number;
  /** Fail the flow when the response status is >= 400. */
  failOnError?: boolean;
}

export interface TransformJsData {
  /** Body of an async function receiving ({ input, steps, state, user, env }). */
  code: string;
}

export interface MapRule {
  to: string;
  from: string; // template expression
}

export interface TransformMapData {
  source: string; // template expression yielding object or array
  mode: 'object' | 'array';
  rules: MapRule[];
  keepUnmapped?: boolean;
}

export type ComparisonOp =
  | 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte'
  | 'contains' | 'notContains' | 'startsWith' | 'endsWith'
  | 'empty' | 'notEmpty' | 'truthy' | 'falsy' | 'in' | 'notIn';

export interface Condition {
  left: string;
  op: ComparisonOp;
  right?: string;
}

export interface LogicConditionData {
  /** All conditions must pass (and) or any (or). */
  combinator: 'and' | 'or';
  conditions: Condition[];
}

export interface SwitchCase {
  id: string;
  label: string;
  condition: Condition;
}

export interface LogicSwitchData {
  cases: SwitchCase[];
  hasDefault: boolean;
}

export interface LogicForeachData {
  source: string; // template yielding an array
  itemName: string;
  maxIterations?: number;
  /** Run the loop body for every item and collect the last node output. */
  concurrency?: number;
}

export interface StateSetData {
  assignments: { key: string; value: string }[];
}

export interface UtilDelayData {
  ms: number;
}

export interface UtilLogData {
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
}

/**
 * Publishes a message to a project channel that dashboards can subscribe to.
 *
 * The platform stays domain-agnostic: it does not know what a channel carries,
 * only that a flow chose to broadcast it. Whatever a flow publishes is what a
 * `stream` widget charts.
 */
export interface RealtimePublishData {
  /** Free-text channel name, scoped to the project. */
  channel: string;
  /** Template expression producing the message body — an object or an array of them. */
  payload: string;
  /** Also persist to the channel's snapshot ring, so late joiners see history. */
  retain?: boolean;
}

export interface ResponseData {
  status: number;
  bodyMode: 'json' | 'text' | 'template' | 'passthrough';
  /** Template expression or literal JSON depending on bodyMode. */
  body: string;
  headers: Record<string, string>;
  /** Declared shape of the response, drives dashboard + OpenAPI generation. */
  schema?: FieldSpec[];
}

export interface NodeDataMap {
  'trigger.http': TriggerHttpData;
  'auth.guard': AuthGuardData;
  'validate.schema': ValidateSchemaData;
  'datasource.query': DatasourceQueryData;
  'http.request': HttpRequestData;
  'transform.js': TransformJsData;
  'transform.map': TransformMapData;
  'logic.condition': LogicConditionData;
  'logic.switch': LogicSwitchData;
  'logic.foreach': LogicForeachData;
  'state.set': StateSetData;
  'util.delay': UtilDelayData;
  'util.log': UtilLogData;
  'realtime.publish': RealtimePublishData;
  response: ResponseData;
}

export interface FlowNodeBase {
  id: string;
  position: { x: number; y: number };
  label?: string;
  notes?: string;
  disabled?: boolean;
}

export type FlowNode = {
  [K in NodeKind]: FlowNodeBase & { type: K; data: NodeDataMap[K] };
}[NodeKind];

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  label?: string;
}

export interface FlowGraph {
  nodes: FlowNode[];
  edges: FlowEdge[];
  viewport?: Viewport;
}

export const EMPTY_GRAPH: FlowGraph = { nodes: [], edges: [] };

/* ------------------------------------------------------------------ */
/* Entities                                                            */
/* ------------------------------------------------------------------ */

export interface User {
  id: string;
  email: string;
  name: string;
  globalRole: GlobalRole;
  avatarColor: string;
  disabled: boolean;
  createdAt: string;
  lastSeenAt: string | null;
}

export interface Project {
  id: string;
  slug: string;
  name: string;
  description: string;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectMember {
  id: string;
  projectId: string;
  userId: string;
  role: ProjectRole;
  createdAt: string;
  user?: Pick<User, 'id' | 'email' | 'name' | 'avatarColor'>;
}

export type EndpointStatus = 'draft' | 'published';

export interface Endpoint {
  id: string;
  projectId: string;
  name: string;
  description: string;
  method: HttpMethod;
  path: string;
  status: EndpointStatus;
  version: number;
  graph: FlowGraph;
  publishedGraph: FlowGraph | null;
  /** Last successful response body, used to seed dashboard widgets. */
  sampleResponse: Json | null;
  createdAt: string;
  updatedAt: string;
  updatedBy: string | null;
}

export type DatasourceKind = 'postgres' | 'mysql' | 'sqlite' | 'rest';

export interface Datasource {
  id: string;
  projectId: string;
  name: string;
  kind: DatasourceKind;
  /** Secrets are redacted when leaving the server. */
  config: Record<string, Json>;
  createdAt: string;
  updatedAt: string;
}

export type WidgetType =
  | 'stat'
  | 'line'
  | 'bar'
  | 'area'
  | 'pie'
  | 'table'
  | 'gauge'
  | 'markdown';

/**
 * How a `stream` widget turns published messages into rows.
 *
 * Nothing here names a domain. A widget says "one row per distinct `key`" or
 * "one column per distinct `key`", and the emitters — tanks, sensors, workers,
 * anything — are discovered from the messages at render time. Add a tank and it
 * appears in the chart without anyone editing the widget.
 */
export interface StreamSpec {
  /** Channel a `realtime.publish` node broadcasts to. */
  channel: string;
  /**
   * `append` keeps the message log, which is a time series. `latest` keeps one
   * row per distinct `key` — a status table, or a bar per emitter.
   */
  mode: 'append' | 'latest';
  /** Field identifying the emitter. Required for `latest`, and for `pivot`. */
  key?: string;
  /**
   * Pivots the log into one row per x value with a column per distinct `key`,
   * filled with this field — one line per emitter, none of them hardcoded.
   */
  pivot?: string;
  /** Messages retained, client-side and in the server's snapshot ring. */
  window?: number;
}

export interface WidgetSource {
  kind: 'endpoint' | 'datasource' | 'static' | 'stream';
  endpointId?: string;
  datasourceId?: string;
  sql?: string;
  params?: Record<string, string>;
  body?: string;
  staticData?: Json;
  /**
   * For `stream` widgets. The HTTP data route answers with a snapshot built by
   * the same code the browser runs, so the widget paints immediately and keeps
   * working when the socket cannot connect.
   */
  stream?: StreamSpec;
}

export interface SeriesSpec {
  key: string;
  label?: string;
  color?: string;
}

export interface WidgetMapping {
  /** JSON path into the response that yields the rows array (empty = root). */
  rowsPath?: string;
  /** Category / x-axis field. */
  x?: string;
  /** Numeric series to plot. */
  series?: SeriesSpec[];
  /** Single value field for stat/gauge widgets. */
  value?: string;
  /** Aggregation applied when the source returns many rows but the widget needs one number. */
  aggregate?: 'first' | 'last' | 'sum' | 'avg' | 'min' | 'max' | 'count';
  /** Explicit column list for table widgets. */
  columns?: { key: string; label?: string }[];
  /**
   * Path to the total row count in the payload, for server-side paging. A SQL
   * source usually supplies it with `COUNT(*) OVER () AS total`.
   */
  total?: string;
}

export interface WidgetOptions {
  unit?: string;
  precision?: number;
  /**
   * Highlight values that changed since the last update, for a moment.
   *
   * `row` lights the whole row — right when rows arrive and leave, like a
   * message log. `cell` lights only the figures that moved — right when the
   * rows are stable and it is the numbers that change, like a status table.
   */
  highlight?: 'none' | 'row' | 'cell';
  /**
   * Rows per page for a table. When set, paging happens on the server: the
   * widget's query receives `vars.limit` and `vars.offset`, and only that page
   * crosses the wire. A million-row history stays a million rows in the
   * database instead of arriving in the browser.
   */
  pageSize?: number;
  stacked?: boolean;
  showLegend?: boolean;
  showGrid?: boolean;
  curve?: 'linear' | 'monotone' | 'step';
  min?: number;
  max?: number;
  markdown?: string;
  emptyText?: string;
}

export interface WidgetLayout {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Widget {
  id: string;
  type: WidgetType;
  title: string;
  source: WidgetSource;
  mapping: WidgetMapping;
  options: WidgetOptions;
  layout: WidgetLayout;
  /** Controls rendered in this widget's own header, feeding its variables. */
  filters?: WidgetFilter[];
}

/**
 * Pinned dashboards render live on the project overview, so each one costs a
 * batch widget query per page load. The cap keeps the overview a summary rather
 * than a slow second copy of the dashboard list.
 */
export const MAX_PINNED_DASHBOARDS = 4;

/**
 * A control that belongs to one widget, whose value is passed to that widget's
 * query as a template variable.
 *
 * Deliberately per widget rather than per dashboard: a page usually mixes a
 * live feed, a summary and a history table, and a single global date range is
 * wrong for at least two of them. A widget declares what it can be filtered by,
 * and the platform never interprets the value — the widget's own SQL or
 * endpoint parameters decide what `{{ vars.<id> }}` means.
 */
export interface WidgetFilter {
  /** Variable name. A `dateRange` exposes `<id>From` and `<id>To`. */
  id: string;
  label: string;
  type: 'dateRange' | 'select' | 'text';
  /** Preset key for a date range, or the initial value for the other types. */
  default?: string;
  options?: { value: string; label: string }[];
}

/** Named windows a date-range filter offers, newest-relative. */
export const DATE_RANGE_PRESETS = [
  { value: '15m', label: 'Oxirgi 15 daqiqa', minutes: 15 },
  { value: '1h', label: 'Oxirgi 1 soat', minutes: 60 },
  { value: '6h', label: 'Oxirgi 6 soat', minutes: 360 },
  { value: '24h', label: 'Oxirgi 24 soat', minutes: 1440 },
  { value: '7d', label: 'Oxirgi 7 kun', minutes: 10080 },
  { value: '30d', label: 'Oxirgi 30 kun', minutes: 43200 },
] as const;

export type DateRangePreset = (typeof DATE_RANGE_PRESETS)[number]['value'];

/**
 * Resolves a filter's current selection into the variables widgets template
 * against. Shared so the dashboard page and any future scheduled export agree
 * on what "the last 24 hours" means.
 */
export function resolveDateRange(
  value: string,
  custom?: { from?: string; to?: string },
  now = new Date(),
): { from: string; to: string } {
  if (value === 'custom' && custom?.from && custom?.to) {
    return { from: custom.from, to: custom.to };
  }
  const preset = DATE_RANGE_PRESETS.find((p) => p.value === value) ?? DATE_RANGE_PRESETS[3];
  return {
    from: new Date(now.getTime() - preset.minutes * 60_000).toISOString(),
    to: now.toISOString(),
  };
}

export interface Dashboard {
  id: string;
  projectId: string;
  name: string;
  description: string;
  widgets: Widget[];
  /** Auto refresh interval in ms. 0 disables polling (realtime pushes still apply). */
  refreshMs: number;
  minRole: ProjectRole;
  /**
   * When this dashboard was pinned to the project overview, or `null` if it is
   * not pinned. One column carries both the flag and the order, so pins render
   * oldest-first without a separate sort key.
   */
  pinnedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const TOKEN_SCOPES = [
  'api:invoke',
  'endpoints:read',
  'endpoints:write',
  'dashboards:read',
  'dashboards:write',
  'datasources:read',
  'datasources:write',
  'logs:read',
  'members:read',
] as const;

export type TokenScope = (typeof TOKEN_SCOPES)[number];

export interface ApiToken {
  id: string;
  projectId: string;
  name: string;
  prefix: string;
  scopes: TokenScope[];
  role: ProjectRole;
  createdBy: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export type RunStatus = 'success' | 'error' | 'timeout';

export interface StepTrace {
  nodeId: string;
  type: NodeKind;
  label: string;
  startedAt: number;
  durationMs: number;
  status: 'success' | 'error' | 'skipped';
  output?: Json;
  error?: string;
}

export interface RunLog {
  id: string;
  projectId: string;
  endpointId: string;
  endpointName: string;
  method: HttpMethod;
  path: string;
  status: RunStatus;
  httpStatus: number;
  durationMs: number;
  actor: string;
  actorKind: 'user' | 'token' | 'anonymous';
  error: string | null;
  trace: StepTrace[];
  createdAt: string;
}

/* ------------------------------------------------------------------ */
/* Auth payloads                                                       */
/* ------------------------------------------------------------------ */

export interface SessionUser extends User {
  memberships: { projectId: string; role: ProjectRole }[];
}

export interface AuthResponse {
  accessToken: string;
  expiresIn: number;
  user: SessionUser;
}

/* ------------------------------------------------------------------ */
/* Realtime events                                                     */
/* ------------------------------------------------------------------ */

export interface PresencePeer {
  socketId: string;
  userId: string;
  name: string;
  color: string;
  cursor?: { x: number; y: number } | null;
  selection?: string[];
}

export type ServerEvent =
  | { type: 'graph:patch'; endpointId: string; graph: FlowGraph; version: number; by: string }
  | { type: 'presence:sync'; room: string; peers: PresencePeer[] }
  | { type: 'entity:changed'; entity: string; projectId: string; id: string; action: 'created' | 'updated' | 'deleted' }
  | { type: 'run:log'; log: RunLog }
  | { type: 'dashboard:data'; dashboardId: string; widgetId: string; data: Json; at: string }
  | { type: 'dashboard:invalidate'; dashboardId: string };


/* --------------------------- realtime stream rows ------------------------- */

/** Default number of messages a stream widget keeps. */
export const STREAM_WINDOW = 150;

function asRow(message: Json): Record<string, Json> | null {
  return message !== null && typeof message === 'object' && !Array.isArray(message)
    ? (message as Record<string, Json>)
    : null;
}

/**
 * Turns a channel's message log into widget rows.
 *
 * Shared on purpose: the server builds the first-paint snapshot with it and the
 * browser extends that from the live stream. One implementation means the chart
 * cannot jump when the socket connects.
 */
export function streamRows(
  messages: Json[],
  spec: StreamSpec,
  xField?: string,
): Record<string, Json>[] {
  const rows = messages.map(asRow).filter((row): row is Record<string, Json> => row !== null);

  if (spec.mode === 'latest') {
    if (!spec.key) return rows;
    const newest = new Map<string, Record<string, Json>>();
    for (const row of rows) newest.set(String(row[spec.key] ?? ''), row);
    // Sorted by key so a table or bar chart never reshuffles between ticks.
    return [...newest.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([, row]) => row);
  }

  if (spec.pivot && spec.key && xField) {
    const byX = new Map<string, Record<string, Json>>();
    for (const row of rows) {
      const x = String(row[xField] ?? '');
      const bucket = byX.get(x) ?? { [xField]: x };
      bucket[String(row[spec.key] ?? '')] = row[spec.pivot] ?? null;
      byX.set(x, bucket);
    }
    // Messages arrive in order and Map keeps insertion order, so this is chronological.
    return [...byX.values()];
  }

  return rows;
}

/**
 * Series a chart should draw when the widget does not name any: every numeric
 * column except the x axis. This is what makes a pivoted stream chart adapt to
 * emitters appearing and disappearing.
 */
export function discoverSeries(rows: Record<string, Json>[], xField?: string): string[] {
  const keys = new Set<string>();
  for (const row of rows) {
    for (const [key, value] of Object.entries(row)) {
      if (key === xField) continue;
      if (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value)))) {
        keys.add(key);
      }
    }
  }
  return [...keys].sort((a, b) => a.localeCompare(b));
}
